import { useCallback, useEffect, useState } from "react";
import { Download, Search } from "lucide-react";
import { API_BASE, api, apiError, formatDate, formatDateTime } from "@/lib/api";
import { Seo } from "@/components/Seo";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminField,
  AdminPageHead,
  AdminSpinner,
  AdminTable,
  Drawer,
  EmptyState,
  FilterTabs,
  StatusBadge,
  adminInputClass,
} from "@/components/admin/AdminUi";

const TABS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "under_review", label: "Under Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "archived", label: "Archived" },
];

const NEXT_STATUS = ["new", "under_review", "approved", "rejected", "archived"];

export default function Nominations() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  const load = useCallback(() => {
    api
      .get("/admin/emerald/nominations", { params: { status, q: q || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => {
        setData({ items: [], counts: {} });
        setBanner({ status: "error", message: apiError(e) });
      });
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const changeStatus = async (id, newStatus) => {
    try {
      const { data: updated } = await api.patch(`/admin/emerald/nominations/${id}/status`, {
        status: newStatus,
      });
      setSelected(updated);
      setBanner({ status: "ok", message: `Status updated to "${newStatus.replace(/_/g, " ")}".` });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data: updated } = await api.post(`/admin/emerald/nominations/${selected.id}/notes`, {
        note: note.trim(),
      });
      setSelected(updated);
      setNote("");
      setBanner({ status: "ok", message: "Internal note added." });
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  return (
    <div data-testid="cms-nominations">
      <Seo title="Emerald Honours Nominations | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Emerald Honours Nominations"
        subtitle="Review and manage all nominations. Nothing is public until it is approved."
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <label htmlFor="nom-search" className="sr-only">
            Search nominations
          </label>
          <input
            id="nom-search"
            data-testid="cms-nominations-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search nominee or nominator…"
            className={`${adminInputClass} w-56 pl-9`}
          />
        </div>
        <a
          href={`${API_BASE}/admin/emerald/nominations/export.csv${
            status !== "all" ? `?status=${status}` : ""
          }`}
          data-testid="cms-nominations-export"
        >
          <AdminButton variant="ghost">
            <Download className="h-4 w-4" /> Export CSV
          </AdminButton>
        </a>
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <FilterTabs
        tabs={TABS}
        value={status}
        onChange={setStatus}
        counts={data?.counts || {}}
        testIdPrefix="cms-nominations-filter"
      />

      <AdminCard className="overflow-hidden">
        {data === null ? (
          <AdminSpinner label="Loading nominations" />
        ) : data.items.length === 0 ? (
          <EmptyState
            message="No nominations in this view yet. Submissions from the public nomination form appear here."
            testId="cms-nominations-empty"
          />
        ) : (
          <AdminTable
            columns={["Nominee", "Category", "Submitted By", "Date", "Status", "Actions"]}
            testId="cms-nominations-table"
          >
            {data.items.map((n) => (
              <tr key={n.id} className="transition-colors hover:bg-slate-50/70" data-testid={`cms-nomination-row-${n.id}`}>
                <td className="px-5 py-4 font-bold text-bka-navy">{n.nominee_name}</td>
                <td className="px-5 py-4 capitalize text-slate-500">
                  {String(n.category || "—").replace(/-/g, " ")}
                </td>
                <td className="px-5 py-4 text-slate-500">{n.nominator_name}</td>
                <td className="px-5 py-4 text-slate-500">{formatDate(n.created_at)}</td>
                <td className="px-5 py-4">
                  <StatusBadge status={n.status} testId={`cms-nomination-status-${n.id}`} />
                </td>
                <td className="px-5 py-4">
                  <AdminButton
                    variant="ghost"
                    testId={`cms-nomination-view-${n.id}`}
                    onClick={() => {
                      setSelected(n);
                      setNote("");
                    }}
                    className="px-3 py-1.5 text-xs"
                  >
                    Review
                  </AdminButton>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminCard>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Nomination Details"
        testId="cms-nomination-drawer"
      >
        {selected && (
          <div className="space-y-7">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-display text-xl font-bold text-bka-navy">{selected.nominee_name}</h3>
                <p className="mt-1 text-sm capitalize text-slate-500">
                  {String(selected.category || "No category").replace(/-/g, " ")}
                </p>
              </div>
              <StatusBadge status={selected.status} testId="cms-nomination-drawer-status" />
            </div>

            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Change Status</p>
              <div className="flex flex-wrap gap-2">
                {NEXT_STATUS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`cms-nomination-set-${s}`}
                    onClick={() => changeStatus(selected.id, s)}
                    disabled={selected.status === s}
                    className={`rounded-xl px-3.5 py-2 text-xs font-bold capitalize transition-colors ${
                      selected.status === s
                        ? "cursor-default bg-bka-navy text-white"
                        : "border border-slate-200 text-slate-600 hover:border-bka-emerald hover:text-bka-emerald"
                    }`}
                  >
                    {s.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </div>

            <dl className="divide-y divide-slate-100 rounded-2xl border border-slate-200 text-sm">
              {[
                ["Nominee bio / background", selected.nominee_bio || "—"],
                ["Reason for nomination", selected.nomination_reason],
                ["Nominator", selected.nominator_name],
                ["Nominator email", selected.nominator_email],
                ["Nominator phone", selected.nominator_phone || "—"],
                ["Consent acknowledged", selected.consent_acknowledged ? "Yes" : "No"],
                ["Submitted", formatDateTime(selected.created_at)],
                [
                  "Last reviewed",
                  selected.reviewed_by
                    ? `${selected.reviewed_by} · ${formatDateTime(selected.reviewed_at)}`
                    : "Not yet reviewed",
                ],
              ].map(([label, value]) => (
                <div key={label} className="px-4 py-3">
                  <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</dt>
                  <dd className="mt-1 whitespace-pre-wrap text-bka-ink">{value}</dd>
                </div>
              ))}
            </dl>

            <div>
              <h4 className="mb-3 font-display text-base font-bold text-bka-navy">Internal Notes</h4>
              <div className="space-y-2">
                {(selected.internal_notes || []).length === 0 && (
                  <p className="text-sm text-slate-500">No internal notes yet.</p>
                )}
                {(selected.internal_notes || []).map((n, i) => (
                  <div key={i} className="rounded-xl bg-slate-50 p-4" data-testid={`cms-nomination-note-${i}`}>
                    <p className="text-sm text-bka-ink">{n.note}</p>
                    <p className="mt-2 text-xs text-slate-400">
                      {n.author} · {formatDateTime(n.created_at)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-3">
                <AdminField label="Add an internal note" htmlFor="nom-note">
                  <textarea
                    id="nom-note"
                    data-testid="cms-nomination-note-input"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className={adminInputClass}
                    placeholder="Notes are visible to CMS users only, never to the public."
                  />
                </AdminField>
                <AdminButton testId="cms-nomination-note-submit" onClick={addNote} disabled={!note.trim()}>
                  Add Note
                </AdminButton>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
